import React from "react";
import classNames from "classnames";
import { Card, CardBody, Col, Nav, NavItem, NavLink, Row } from "reactstrap";

export type KpiTabItem = {
  label: string;
  value: string;
  detalle: string;
  icono: string;
  color: string;
  valueTooltip?: string;
};

export type KpiTabConfig = {
  id: string;
  label: string;
  items: KpiTabItem[];
};

interface KpiTabsSectionProps {
  tabs: KpiTabConfig[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  navClassName?: string;
}

interface KpiTabsSectionProps {
  tabs: KpiTabConfig[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  navClassName?: string;
  isLoading?: boolean;
  loadingMessage?: string;
}

const KpiTabsSection = ({
  tabs,
  activeTab,
  onTabChange,
  navClassName,
  isLoading = false,
  loadingMessage = "Cargando indicadores...",
}: KpiTabsSectionProps) => {
  if (tabs.length === 0) {
    return null;
  }

  const handleNavClick = (event: React.MouseEvent<HTMLAnchorElement>, nextTab: string) => {
    event.preventDefault();
    onTabChange(nextTab);
  };

  const activeItems = tabs.find((tab) => tab.id === activeTab)?.items ?? [];
  const navClasses = classNames("nav nav-tabs nav-border-top mb-3", navClassName);

  return (
    <>
      <Nav tabs className={navClasses}>
        {tabs.map((tab) => (
          <NavItem key={tab.id}>
            <NavLink
              href="#"
              className={classNames({ active: activeTab === tab.id })}
              style={{ cursor: "pointer" }}
              onClick={(event) => handleNavClick(event, tab.id)}
            >
              {tab.label}
            </NavLink>
          </NavItem>
        ))}
      </Nav>
      {isLoading ? (
        <Row className="g-3 mb-3 row-cols-1 row-cols-sm-2 row-cols-lg-3 row-cols-xl-4 row-cols-xxl-5">
          {Array.from({ length: 5 }).map((_, index) => (
            <Col key={`kpi-skeleton-${index}`}>
              <Card className="shadow-none border h-100">
                <CardBody>
                  <div className="d-flex align-items-center gap-3">
                    <div className="skeleton-pulse skeleton-circle" />
                    <div className="flex-grow-1 d-flex flex-column gap-2">
                      <div className="skeleton-pulse skeleton-line w-75" />
                      <div className="skeleton-pulse skeleton-line w-50" style={{ height: 14 }} />
                    </div>
                  </div>
                  <div className="skeleton-pulse skeleton-line mt-3 w-50" />
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      ) : (
        <Row className="g-3 mb-3 row-cols-1 row-cols-sm-2 row-cols-lg-3 row-cols-xl-4 row-cols-xxl-5">
          {activeItems.map((kpi) => (
            <Col key={`${activeTab}-${kpi.label}`}>
              <Card className="shadow-none border h-100">
                <CardBody>
                  <div className="d-flex align-items-center mb-2">
                    <div className={`avatar-sm flex-shrink-0 me-3 bg-${kpi.color}-subtle text-${kpi.color} rounded`}>
                      <div className="avatar-title rounded fs-4">
                        <i className={kpi.icono}></i>
                      </div>
                    </div>
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                      <p className="text-muted text-uppercase fs-12 mb-1">{kpi.label}</p>
                      <h4
                        className="mb-0 text-break"
                        style={{ wordBreak: "break-word" }}
                        title={kpi.valueTooltip ?? kpi.value}
                      >
                        {kpi.value}
                      </h4>
                    </div>
                  </div>
                  <small className="text-muted">{kpi.detalle}</small>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </>
  );
};

export default KpiTabsSection;
